import { AsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';
import { useAppDispatch } from 'store';
import { DefaultRejectValue, ExtraParamsThunkType } from 'types';

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;

  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && typeof message[0] === 'string' && message[0].trim()) return message[0];
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const useAsyncAction = <Returned, ThunkArg, RejectValue = DefaultRejectValue>(
  action: AsyncThunk<Returned, ThunkArg, ExtraParamsThunkType<RejectValue>>,
) => {
  const [isExecuting, setIsExecuting] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const execute = useCallback(
    async (actionPayload?: ThunkArg) => {
      try {
        setIsExecuting(true);
        const payload = (actionPayload || undefined) as ThunkArg;
        const thunkResult = await dispatch(action(payload));
        try {
          return unwrapResult(thunkResult);
        } catch (e: unknown) {
          const message = getErrorMessage(e) || 'Something went wrong';
          throw new Error(message, { cause: e });
        }
      } finally {
        setIsExecuting(false);
      }
    },
    [action, dispatch],
  );

  return [execute, isExecuting] as const;
};

export default useAsyncAction;
